/**
 * 3DS whole process FSM
 */
export enum THREE_DS_STATUS {
  /**
   * DDC Collecting & Uploading process, only in standalone 3ds
   * 1. DDC_UNSTARTED -- initial status
   * 2. DDC_COLLECTING -- ddc collecting by channel iframe
   * 3. DDC_UPLOADING -- upload ddc result to GN
   * 4. DDC_UPLOAD_SUCCESS -- successfully uploaded.
   *    -> Next status: 1) frictionless -> RESULT_POLLING 2) challenge -> STEPUP(super 3ds) | channel page(previous)
   * 5. DDC_UPLOAD_FAILED -- API failed | timeout
   * 6. DDC_RETRY -- retry status, not available now
   * 7. DDC_ERROR -- errors state in ddc process
   */
  DDC_UNSTARTED = "ddc_unstarted",
  DDC_COLLECTING = "ddc_collecting",
  DDC_UPLOADING = "ddc_uploading",
  DDC_UPLOAD_SUCCESS = "ddc_upload_success",
  DDC_UPLOAD_FAILED = "ddc_upload_failed",
  DDC_RETRY = "ddc_retry",
  DDC_ERROR = "ddc_error",

  /**
   * Stepup process
   * Only for super3ds
   */
  STEPUP = "stepup",

  /**
   * Verify result upload process
   * 1. VERIFY_UPLOAD_UNSTARTED -- initial status
   * 2. VERIFY_UPLOADING -- 3ds result uploading
   * 3. VERIFY_UPLOAD_ERROR -- API failed | timeout
   * 4. VERIFY_UPLOAD_SUCCESS -- successfully uploaded.
   *    -> Next status: RESULT_POLLING
   */
  VERIFY_UPLOAD_UNSTARTED = "verify_upload_unstarted",
  VERIFY_UPLOADING = "verify_uploading",
  VERIFY_UPLOAD_ERROR = "verify_upload_error",
  VERIFY_UPLOAD_SUCCESS = "verify_upload_success",

  /**
   * Result polling process
   * 1. RESULT_POLLING: polling result
   * 2. RESULT_POLLING_ERROR: API failed | timeout
   * 3. RESULT_POLLING_STOPPED: Excceed 150 requests
   * 4. RESULT_SUCCESS: resp is success
   * 5. RESULT_FAILED: resp is failed
   */
  RESULT_POLLING = "result_polling",
  RESULT_POLLING_ERROR = "result_polling_error",
  RESULT_POLLING_STOPPED = "result_polling_stopped",
  RESULT_SUCCESS = "result_success",
  RESULT_FAILED = "result_failed",
}

/**
 * 3ds ddc hook 内部状态
 * 1. collecting 3ds信息收集过程
 * 2. uploading 3ds收集信息上传过程
 * 3. success/failed 上传结果成功、失败
 * 4. error 上传过程出错
 * 5. retry 网络错误或显式返回重试
 * 6. polling 查单，frictionless需要加上查单
 */
export enum THREE_DS_DDC_UPLOAD_STATUS {
  unstarted = "unstarted",
  collecting = "collecting",
  uploading = "uploading",
  success = "success",
  failed = "failed",
  error = "error",
  retry = "retry",
  polling = "polling",
}

export const THREE_DS_DDC_TIMEOUT = 8000;
export const THREE_DS_DDC_UPLOAD_TIMEOUT = 13000;

/**
 * 用于控制 [支付/绑卡结果] 轮询中间页的展示状态
 * 1. success 请求成功，且支付绑卡成功
 * 2. fail 请求成功，且支付绑卡失败
 * 3. pending 请求成功，支付绑卡结果 pending
 * 4. error 请求失败（包含http error、接口error）
 * 5. pollingStopped 请求成功，轮询超过最大限制，停止轮询
 * 6. redirect 请求成功，支付绑卡结果 pending，此时 redirect_details 字段不为空，可跳转渠道确认页
 */
export enum PAY_OR_BIND_RESULT_STATUS {
  success = "success",
  fail = "fail",
  pending = "pending",
  error = "error",
  pollingStopped = "pollingStopped",
  redirect = "redirect",
}

/**
 * [支付/绑卡结果] 轮询中间页的配置优化模式
 * 后端修改：只有 landpage_with_pending_state 才会在 url 拼接参数；
 * 1. landpage_with_pending_state 业务方需要中间页，业务方除了可以处理 success/fail 状态，也可以处理 pending 状态，若配置此模式，轮训超过最大次数仍为 pending 则跳回 return_url 并携带 pending 状态，Hosted-page 在 pending 结果页点击 [back to merchant] 返回 success_url 并携带 pending 状态
 */
export enum LANDPAGE_MODE {
  landpage_with_pending_state = "landpage_with_pending_state",
}
/**
 * 3ds standalone提供商
 */
export enum THREEDS_CHANNEL_NAME {
  Cybersource = "Cybersource",
  Mastercard = "Mastercard",
}
/**
 * 3ds method notification
 */
export const THREEDS_MESSAGE_ORIGIN = {
  [THREEDS_CHANNEL_NAME.Cybersource]: {
    dev: "https://centinelapistag.cardinalcommerce.com",
    prod: "https://centinelapi.cardinalcommerce.com",
  },
  [THREEDS_CHANNEL_NAME.Mastercard]: {
    dev: location.origin,
    prod: location.origin,
  },
};
/**
 * 错误页的状态（适用于通用错误页与404错误页）
 * 1. back_to_merchant: 未拿到订单信息，但拿到商户 error_url 能够回到商户页面
 * 2. need_retry: 未拿订单详情以及 error_url, 需要用户 retry 再次尝试获取
 * 3. query_order: 错误页为了获取订单详情需要进行查单
 * 4. no_order_detail(默认值): 错误页获取不到任何订单信息
 */
export enum FAULT_STATE {
  back_to_merchant = "back_to_merchant",
  need_retry = "need_retry",
  query_order = "query_order",
  no_order_detail = "no_order_detail",
}

/**
 * 渠道要求展示的security logo
 * pmid => url
 */
export const THREEDS_SECURITY_LOGO = {
  pm_pi_ccdc_visa_c_d:
    "https://lf16-co.g-p-static.com/obj/pipo-sgcompliance/sky/VISA_light_c6fab6.png",
  pm_pi_ccdc_amex_c_d:
    "https://lf16-co.g-p-static.com/obj/pipo-sgcompliance/sky/AMEX_light_06c6c2.png",
  pm_pi_ccdc_jcb_c_d:
    "https://lf16-co.g-p-static.com/obj/pipo-sgcompliance/sky/JCB_adc3f6.png",
  pm_pi_ccdc_mastercard_c_d:
    "https://lf16-co.g-p-static.com/obj/pipo-sgcompliance/sky/Master_5ffd9d.png",
};
